import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import InfoCircle from '@/images/circles/InfoCirclePrimary.svg';
import TagHorizontal from '@/images/TagHorizontal.svg';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { ttsGenerationErrors } from '@/state/presenters';
import theme from '@/theme';
import { MixPanelEvent } from '@/utils/types';
import { annoynmousUserToEditorPagePostSsoReturnTo } from '@/utils/utils';
import styled from '@emotion/styled';
import { reaction, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { PagesTimeline } from 'polotno/pages-timeline';
import { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { useAdvancedEditorContext } from '../../../context/useAdvancedEditorContext';
import {
    getPageProperty,
    PolotnoStoreActivePageKeys,
    setPageProperty,
    useCurrentProjectId,
    usePolotnoActivePageState,
} from '../../../hooks';
import { InvalidTextLanguageError } from '../../../hooks/scenesPreview/InvalidTextLanguageError';
import { useScenesPreview } from '../../../hooks/scenesPreview/useScenesPreview';
import { useScenesPageActionsMixpanel } from '../../../hooks/useScenesPageActionsMixpanel';
import { SIDEBAR_PANELS } from '../../../types';
import { useUpdatePagesThumbnails } from '../useUpdatePagesThumbnails';
import './scene-popup.css';
import { TimelineStyleWrapper } from './TimelineStyleWrapper';

const AnimationsIndicatorWrapper = styled.div`
    position: fixed;
    bottom: 130px;
    color: ${theme.colors.secondary.main};
    background-color: ${theme.colors.white};
    font-weight: 600;
    font-size: 14px;
    box-shadow:
        1.1px 2.7px 3.8px -1.2px #bbbbbb42,
        0.5px 1.3px 1.8px -0.6px #bbbbbb2e,
        0.3px 0.8px 1.1px 0px #bbbbbb1c;
    border-radius: 8px;
    padding: 8px 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    left: 54%;
`;

export const Timeline = observer(() => {
    const { store, saveArtifacts, setActivePanel } = useAdvancedEditorContext();
    const [playLoading, setPlayLoading] = useState(false);
    const audioRef = useRef<ReactPlayer>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const pageRef = useRef<string | null>(null);
    const { isAnonymous, loginWithRedirect } = useAuth();

    const projectId = useCurrentProjectId();
    const { previewReady, preparePreview, validateScenesNotEmpty, sceneShouldIndicateMissingScripts } = useScenesPreview();
    const [cachedAudio] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.cachedAudio);
    const mixpanel = useAnalytics();
    const thumbnails = useUpdatePagesThumbnails();
    const timelineNoDurationEnabled = useFlag('timeline_no_duration_enabled', false);

    const sceneAreDurationsCalculated = store.pages.map(
        p => !timelineNoDurationEnabled || getPageProperty(p, PolotnoStoreActivePageKeys.sceneDurationCalculated)
    );
    const sceneDurations = store.pages.map(p => p.duration);
    useScenesPageActionsMixpanel();

    useEffect(() => {
        const dispose = reaction(
            () => store.isPlaying,
            isPlaying => {
                if (isPlaying && !previewReady) {
                    setPlayLoading(true);
                }
            }
        );

        return () => {
            dispose();
        };
    }, [previewReady]);

    const setTimestamp = () => {
        const activePageId = pageRef.current;
        const activePage = store.pages.find(page => page.id === activePageId);

        store.setCurrentTime(activePage?.startTime || 0);
    };

    // This useEffect is responsible for updating the duration of the pages before playing the audio
    useEffect(() => {
        const dispose = reaction(
            () => store.isPlaying,
            async isPlaying => {
                // set initial page & timestamp
                store.selectPage(pageRef.current);
                setTimestamp();

                if (!isPlaying) {
                    return;
                }

                mixpanel?.track('video-editor-play', {
                    event: MixPanelEvent.CLICK,
                    videoId: projectId,
                });

                if (!validateScenesNotEmpty()) {
                    toast.error({
                        title: 'Some scenes are missing a script',
                    });
                    setPlayLoading(false);
                    store.stop();
                    return;
                }

                if (isAnonymous) {
                    store.stop();
                    setPlayLoading(false);
                    await saveArtifacts(store);
                    loginWithRedirect({
                        appState: { returnTo: annoynmousUserToEditorPagePostSsoReturnTo },
                    });
                    return;
                }

                if (previewReady) {
                    return;
                }

                runInAction(async () => {
                    store.stop();
                    try {
                        await preparePreview();
                        store.play();
                    } catch (e) {
                        if (e instanceof InvalidTextLanguageError) {
                            const page = e.page;
                            store.selectPage(page.id);

                            setActivePanel(SIDEBAR_PANELS.SCRIPT);
                            setPageProperty(page, PolotnoStoreActivePageKeys.ttsError)(ttsGenerationErrors['language']);
                        } else {
                            toast.error({
                                title: 'Video preview error',
                                message: "Failed to generate the avatar's voice. Please try again.",
                            });
                        }
                        logger.error("Couldn't update scenes duration", e);
                    }
                    setPlayLoading(false);
                });
            }
        );

        return () => {
            dispose();
        };
    }, [previewReady, preparePreview]);

    // Stopping the audio on any change in the store
    useEffect(() => {
        const dispose = store.on('change', () => {
            if (store.isPlaying) {
                store.stop();
            }
        });

        return () => {
            dispose();
        };
    }, []);

    useEffect(() => {
        // add click listener to the selector  polotno-page-container
        if (!store?.isPlaying) {
            return;
        }

        const selector = '.polotno-page-container';
        const pageContainers = document.querySelectorAll(selector);

        const eventListener = () => {
            store.stop();
        };

        pageContainers.forEach(container => {
            container.addEventListener('click', eventListener);
        });

        return () => {
            pageContainers.forEach(container => {
                container.removeEventListener('click', eventListener);
            });
        };
    }, [store?.isPlaying]);

    if (!store.pages.length) {
        return null;
    }

    useEffect(() => {
        const button = wrapperRef.current?.querySelector('.polotno-play-button-container .bp5-button') as HTMLButtonElement | null;

        if (button) {
            // Save the original onclick handler
            const originalOnClick = button.onclick;

            // Add a new onclick handler
            button.onclick = event => {
                // Call the original onclick handler if it exists
                if (originalOnClick) {
                    originalOnClick.call(button, event);
                }

                // Save page id during play click
                pageRef.current = store.activePage?.id;
            };
        }
    }, []);

    return (
        <>
            {store.isPlaying && (
                <AnimationsIndicatorWrapper>
                    <MaskedIcon src={InfoCircle} color={theme.colors.secondary.main} size="24px" />
                    Avatar animations are available after generating
                </AnimationsIndicatorWrapper>
            )}
            <TimelineStyleWrapper
                ref={wrapperRef}
                timeIndicator={TagHorizontal}
                playLoading={playLoading}
                thumbnails={thumbnails}
                sceneAreDurationsCalculated={sceneAreDurationsCalculated}
                sceneShouldIndicateMissingScripts={sceneShouldIndicateMissingScripts}
                sceneDurations={sceneDurations}>
                <PagesTimeline store={store} defaultOpened />
                {store?.isPlaying && (
                    <ReactPlayer
                        ref={audioRef}
                        style={{ display: 'none' }}
                        url={cachedAudio}
                        controls={false}
                        playing={store?.isPlaying}
                        progressInterval={1}
                        onError={e => {
                            store.stop();
                            logger.error('Error while playing audio', e);
                            toast.error({ title: 'Video preview error', message: "Could not play the avatar's voice. Please try again." });
                        }}
                    />
                )}
            </TimelineStyleWrapper>
        </>
    );
});
